import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowLeft, Upload, X, Image as ImageIcon, Video, Link as LinkIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Safely extract a human-readable error message from an axios error.
 * FastAPI 422 responses have `detail` as an ARRAY of {type, loc, msg, ...} objects,
 * which breaks React if passed directly as a toast child.
 */
const extractErrorMessage = (error, fallback = 'Something went wrong') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return error?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
        return field ? `${field}: ${d?.msg || 'invalid'}` : (d?.msg || 'invalid');
      })
      .join('; ');
  }
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
};

const AdminProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [images, setImages] = useState([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoFile, setVideoFile] = useState('');
  const [videoType, setVideoType] = useState('url'); // 'url' or 'upload'
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    sku: '',
    price: '',
    sale_price: '',
    stock_status: 'in_stock',
    inventory_quantity: '0',
    weight_oz: '2.0',
    short_description: '',
    full_description: '',
    features: '',
    customization_notes: '',
    shipping_notes: '',
    tags: '',
    seo_title: '',
    seo_description: '',
    is_featured: false,
    is_active: true
  });

  useEffect(() => {
    if (isEdit) {
      fetchProduct();
    }
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products/${id}`);
      const product = response.data;
      
      setFormData({
        title: product.title,
        category: product.category,
        sku: product.sku,
        price: product.price.toString(),
        sale_price: product.sale_price ? product.sale_price.toString() : '',
        stock_status: product.stock_status,
        inventory_quantity: product.inventory_quantity ? product.inventory_quantity.toString() : '0',
        weight_oz: product.weight_oz ? product.weight_oz.toString() : '2.0',
        short_description: product.short_description,
        full_description: product.full_description,
        features: product.features.join('\n'),
        customization_notes: product.customization_notes,
        shipping_notes: product.shipping_notes,
        tags: product.tags.join(', '),
        seo_title: product.seo_title,
        seo_description: product.seo_description,
        is_featured: product.is_featured,
        is_active: product.is_active
      });
      
      // Set existing images
      setImages(product.images || []);
    } catch (error) {
      toast.error('Failed to fetch product');
      console.error('Error fetching product:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null,
        inventory_quantity: parseInt(formData.inventory_quantity) || 0,
        weight_oz: parseFloat(formData.weight_oz) || 2.0,
        features: formData.features.split('\n').filter(f => f.trim()),
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
        video_url: videoType === 'url' ? videoUrl : null
      };

      if (isEdit) {
        await axios.put(
          `${API_URL}/api/products/admin/${id}`,
          productData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Product updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/products/admin/create`,
          productData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Product created successfully');
      }

      navigate('/admin/products');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to save product'));
      console.error('Error saving product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (images.length + files.length > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }

    if (!isEdit && files.length > 0) {
      toast.error('Please save the product first before uploading images');
      return;
    }

    setUploadingImages(true);

    try {
      const token = localStorage.getItem('admin_token');
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
          `${API_URL}/api/products/admin/${id}/upload-image`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        setImages(prev => [...prev, response.data.image_url]);
      }

      toast.success(`${files.length} image(s) uploaded successfully`);
    } catch (error) {
      toast.error('Failed to upload images');
      console.error('Error uploading images:', error);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = async (imageUrl, index) => {
    if (!isEdit) {
      setImages(prev => prev.filter((_, i) => i !== index));
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      
      // Remove from database
      await axios.put(
        `${API_URL}/api/products/admin/${id}`,
        { images: images.filter((_, i) => i !== index) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setImages(prev => prev.filter((_, i) => i !== index));
      toast.success('Image removed');
    } catch (error) {
      toast.error('Failed to remove image');
      console.error('Error removing image:', error);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!isEdit) {
      toast.error('Please save the product first before uploading video');
      return;
    }

    // Check file size (50MB limit)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video file too large (max 50MB)');
      return;
    }

    setUploadingVideo(true);

    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/products/admin/${id}/upload-video`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setVideoFile(response.data.video_url);
      setVideoType('upload');
      setVideoUrl('');
      toast.success('Video uploaded successfully');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to upload video'));
      console.error('Error uploading video:', error);
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleRemoveVideo = async () => {
    if (!isEdit) {
      setVideoUrl('');
      setVideoFile('');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      
      await axios.put(
        `${API_URL}/api/products/admin/${id}`,
        { video_url: null, video_file: null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setVideoUrl('');
      setVideoFile('');
      toast.success('Video removed');
    } catch (error) {
      toast.error('Failed to remove video');
      console.error('Error removing video:', error);
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/products')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Products
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEdit ? 'Edit Product' : 'New Product'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <Card>
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Product Images</h2>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4 flex-wrap">
                {images.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={image.startsWith('http') ? image : `${API_URL}${image}`}
                      alt={`Product ${index + 1}`}
                      className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(image, index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                {images.length < 5 && isEdit && (
                  <label className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImages}
                    />
                    {uploadingImages ? (
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                        <span className="text-xs text-gray-500 mt-2">Uploading...</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-gray-400 mb-2" />
                        <span className="text-xs text-gray-500">Add Image</span>
                        <span className="text-xs text-gray-400">({images.length}/5)</span>
                      </>
                    )}
                  </label>
                )}

                {images.length === 0 && !isEdit && (
                  <div className="w-full p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                    <ImageIcon className="w-5 h-5 text-blue-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Save product first to add images</p>
                      <p className="text-xs text-blue-700">You can upload up to 5 images after creating the product</p>
                    </div>
                  </div>
                )}
              </div>

              {isEdit && images.length < 5 && (
                <p className="text-xs text-gray-500">
                  You can upload up to {5 - images.length} more image(s). Supported formats: JPG, PNG, WEBP
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Product Video</h2>
            
            <div className="space-y-4">
              {/* Video Type Selector */}
              <div className="flex gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => setVideoType('url')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                    videoType === 'url'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                  }`}
                >
                  <LinkIcon className="w-4 h-4" />
                  <span className="text-sm font-medium">Video URL</span>
                </button>
                <button
                  type="button"
                  onClick={() => setVideoType('upload')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                    videoType === 'upload'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                  }`}
                >
                  <Upload className="w-4 h-4" />
                  <span className="text-sm font-medium">Upload Video</span>
                </button>
              </div>

              {/* Video URL Input */}
              {videoType === 'url' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Video URL (YouTube, Vimeo, or direct link)
                  </label>
                  <input
                    type="url"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Paste a YouTube, Vimeo, or direct video file URL
                  </p>
                </div>
              )}

              {/* Video Upload */}
              {videoType === 'upload' && (
                <div>
                  {videoFile ? (
                    <div className="relative">
                      <video
                        src={videoFile.startsWith('http') ? videoFile : `${API_URL}${videoFile}`}
                        controls
                        className="w-full max-w-md rounded-lg border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveVideo}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      {!isEdit ? (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                          <Video className="w-5 h-5 text-blue-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-blue-900">Save product first to upload video</p>
                            <p className="text-xs text-blue-700">You can upload a video after creating the product</p>
                          </div>
                        </div>
                      ) : (
                        <label className="block cursor-pointer">
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 hover:bg-blue-50 transition-all">
                            {uploadingVideo ? (
                              <div>
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                                <p className="text-sm text-gray-600">Uploading video...</p>
                              </div>
                            ) : (
                              <>
                                <Video className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-sm font-medium text-gray-700 mb-1">
                                  Click to upload video
                                </p>
                                <p className="text-xs text-gray-500">
                                  MP4, WebM, OGG (max 50MB)
                                </p>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            accept="video/mp4,video/webm,video/ogg,video/quicktime"
                            onChange={handleVideoUpload}
                            className="hidden"
                            disabled={uploadingVideo}
                          />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Video Preview for URL */}
              {videoType === 'url' && videoUrl && (
                <div className="relative">
                  {videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be') ? (
                    <iframe
                      src={videoUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                      className="w-full max-w-md aspect-video rounded-lg"
                      allowFullScreen
                    />
                  ) : videoUrl.includes('vimeo.com') ? (
                    <iframe
                      src={videoUrl.replace('vimeo.com/', 'player.vimeo.com/video/')}
                      className="w-full max-w-md aspect-video rounded-lg"
                      allowFullScreen
                    />
                  ) : (
                    <video
                      src={videoUrl}
                      controls
                      className="w-full max-w-md rounded-lg border-2 border-gray-200"
                    />
                  )}
                  <button
                    type="button"
                    onClick={handleRemoveVideo}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Basic Information</h2>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="col-span-2 md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Product Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                />
              </div>

              <div className="col-span-2 md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">SKU *</label>
                <input
                  type="text"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                />
              </div>

              <div className="col-span-2 md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                >
                  <option value="">Select category</option>
                  <option value="Keychains">Keychains</option>
                  <option value="Signs">Signs</option>
                  <option value="Business Cards">Business Cards</option>
                </select>
              </div>

              <div className="col-span-2 md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Stock Status</label>
                <select
                  name="stock_status"
                  value={formData.stock_status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="in_stock">In Stock</option>
                  <option value="out_of_stock">Out of Stock</option>
                  <option value="pre_order">Pre-Order</option>
                </select>
              </div>

              <div className="col-span-2 md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Inventory Quantity *</label>
                <input
                  type="number"
                  name="inventory_quantity"
                  value={formData.inventory_quantity}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Number of units available in stock</p>
              </div>

              <div className="col-span-2 md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Price *</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                />
              </div>

              <div className="col-span-2 md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Sale Price</label>
                <input
                  type="number"
                  name="sale_price"
                  value={formData.sale_price}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div className="col-span-2 md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Weight (oz) *</label>
                <input
                  type="number"
                  name="weight_oz"
                  value={formData.weight_oz}
                  onChange={handleChange}
                  step="0.1"
                  min="0.1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Product weight for shipping calculations
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Description</h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Short Description</label>
                <textarea
                  name="short_description"
                  value={formData.short_description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Description</label>
                <textarea
                  name="full_description"
                  value={formData.full_description}
                  onChange={handleChange}
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Features (one per line)</label>
                <textarea
                  name="features"
                  value={formData.features}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Premium material&#10;Customizable design&#10;Durable construction"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Settings</h2>
            
            <div className="flex items-center gap-8">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={formData.is_featured}
                  onChange={handleChange}
                  className="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Featured Product</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Active</span>
              </label>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center gap-4">
          <Button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg"
            disabled={loading}
            data-testid="admin-product-save-btn"
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Product' : 'Create Product')}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/products')}
            className="px-8 py-6 text-lg"
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminProductForm;
